import { BehaviorSubject } from 'rxjs';
import { Injectable, InjectionToken, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';
import { UserManagerSettings } from 'oidc-client';
import type { SgAppBranding } from '@sg/pre-app-init';
import { ApiConfigOptionsStore } from '@sg/api/base-api-lib';
import { Writeable } from '@sg/utils';

export interface EnvironmentLanguage {
  key: string;
  globalName: string;
  localName?: string;
  rtl?: boolean;
}

export type Env = 'test' | 'prod' | 'sales' | 'demo';
export interface Environment {
  /**
   * When set to true, the environment is loaded from a remote file (On load vs On build).
   * The file if called environment.json and expected to be in assets folder.
   *
   * The content of the remote object will be merged into the content of the built environment object.
   * This means that ALL existing keys are overwritten.
   */
  async?: boolean;

  production: boolean;
  env: Env;
  oidc: Writeable<UserManagerSettings>;

  /**
   * Policy for renewing a token.
   */
  oidcRenewPolicy: {
    /**
     * The interval to check for expired token (in seconds)
     */
    interval: number;
    /**
     * The lowest limit (in seconds) left for a token before starting a token renew.
     */
    renewWhenExpiresIn: number;

    /**
     * The amount of times to retry a failed silent login attempt
     */
    retries: number;
  };

  api: ApiConfigOptionsStore;

  /**
   * Configuration for websockets (SignalR)
   */
  ws: {
    baseUrl: string;
  };

  i18n: {
    default: string;
    langs: { [key: string]: EnvironmentLanguage };
  };

  loggly: {
    key: string;
    /**
     * Optional tag, if not set will take the tag from the env property on the root of the Environment object.
     */
    tag?: string;
  };

  /**
   * The idle settings.
   * Indexes:
   *   0 - Time to mark as idle
   *   1 - Time to logout (from idle end time)
   *
   * e.g.: [30, 90] will go into idle after 30 secs and logout 90 secs after that... or, logout after total of 120 secs.
   */
  idle: [number, number];

  /**
   * Feature flag info.
   * Each key represents the feature name to turn on or off features.
   */
  featureFlag: { [featureName: string]: boolean };

  /**
   * Hotjar related info.
   * Each key represents the configuration for hotjar.
   */
  hotjar: { [configKey: string]: string };

  /**
   * Heap related info.
   * Each key represents the configuration for Heap.
   */
  heap: { [configKey: string]: string };

  /**
   * Pendo related info.
   * Each key represents the configuration for Pendo.
   */
  pendo: { [apiKey: string]: string };

  /**
   * Mixpanel related info (lender app).
   * `token` is the EU project token; the real value is injected per-environment
   * at runtime via assets/environment.json and is not committed to source.
   */
  mixpanel?: { [configKey: string]: string };

  /**
   * Google analytics info
   * Each key represents the app name to enable analytics on and the value is the GA key.
   */
  ga?: { [appName: string]: string };

  /**
   * Enable MSW (Mock Service Worker) for local development
   * When true, all HTTP requests will be intercepted by MSW handlers
   */
  enableMocking?: boolean;

  /**
   * Metadata information about the build/release
   * This should be injected by the build process and not set in the source code.
   */
  meta?: {
    /** The Build ID (e.g. 20170112.1) */
    id: string;
    /** Release version (e.g. Release-90) */
    version: string;
    /** The commit that was built (e.g. bc0044458ba1d9298) */
    hash: string;
    /** Git branch the build was produced from (e.g. master, feature/x) */
    branch?: string;
  };
}

/**
 * For internal use, to generate a JSON schema file for the Environment type.
 */
export interface EnvironmentSchema extends Environment {
  $schema?: string;
}

export const EnvironmentServiceBase = new InjectionToken('EnvironmentServiceBase');

@Injectable()
export class EnvironmentService<T extends string = string> implements Environment {
  /** Returns the app name without the 'sg-' prefix (i.e: 'lender' instead of 'sg-lender')*/
  get appNameWithoutPrefix(): string {
    return this.appName.substring(3);
  }

  constructor(
    @Inject(EnvironmentServiceBase)
    base: { env: Environment; appName: T; branding: SgAppBranding<T>; deployUrl: string },
    private overlayContainer: OverlayContainer,
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    Object.assign(this, base.env);
    this.appName = base.appName;
    this.deployUrl = base.deployUrl;
    this.branding = base.branding;
    this.theme$ = new BehaviorSubject(this.branding.theme);
    if (base.env?.featureFlag) {
      for (const [key, value] of Object.entries(base.env.featureFlag)) {
        this.featureFlag[key] = this.getBooleanFlag(value);
      }
    }

    this.isAdminProd = this.env === 'prod' && this.appName === 'sg-admin';
    if (this.isAdminProd && this.branding.custom) this.branding.logo = this.branding.custom['adminProdLogo'];
  }

  private readonly renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);

  appName: T;
  deployUrl: string;
  branding: SgAppBranding<T>;

  production: boolean;
  env: Env;
  oidc: UserManagerSettings;
  i18n: {
    default: string;
    langs: { [key: string]: EnvironmentLanguage };
  };

  /**
   * Policy for renewing a token.
   */
  oidcRenewPolicy: {
    /**
     * The interval to check for expired token (in seconds)
     */
    interval: number;
    /**
     * The lowest limit (in seconds) left for a token before starting a token renew.
     */
    renewWhenExpiresIn: number;

    /**
     * The amount of times to retry a failed silent login attempt
     */
    retries: number;
  };

  api: ApiConfigOptionsStore;

  /**
   * Configuration for websockets (SignalR)
   */
  ws: {
    baseUrl: string;
  };

  loggly: {
    key: string;
    /**
     * Optional tag, if not set will take the tag from the env property on the root of the Environment object.
     */
    tag?: string;
  };

  /**
   * The idle settings.
   * Indexes:
   *   0 - Time to mark as idle
   *   1 - Time to logout (from idle end time)
   *
   * e.g.: [30, 90] will go into idle after 30 secs and logout 90 secs after that... or, logout after total of 120 secs.
   */
  idle: [number, number];

  /**
   * Feature flag info.
   * Each key represents the feature name to turn on or off features.
   */
  featureFlag: { [featureName: string]: boolean };

  /**
   * Hotjar related info.
   * Each key represents the configuration for hotjar.
   */
  hotjar: { [configKey: string]: string };

  /**
   * Heap related info.
   * Each key represents the configuration for Heap.
   */
  heap: { [configKey: string]: string };

  /**
   * Pendo related info.
   * Each key represents the configuration for Pendo.
   */
  pendo: { [apiKey: string]: string };

  /**
   * Mixpanel related info (lender app).
   * `token` is the EU project token; the real value is injected per-environment
   * at runtime via assets/environment.json and is not committed to source.
   */
  mixpanel?: { [configKey: string]: string };

  /**
   * Google analytics info
   * Each key represents the app name to enable analytics on and the value is the GA key.
   */
  ga?: { [appName: string]: string };

  /**
   * Metadata information about the build/release
   * This should be injected by the build process and not set in the source code.
   */
  meta?: {
    /** The Build ID (e.g. 20170112.1) */
    id: string;
    /** Release version (e.g. Release-90) */
    version: string;
    /** The commit that was built (e.g. bc0044458ba1d9298) */
    hash: string;
    /** Git branch the build was produced from (e.g. master, feature/x) */
    branch?: string;
  };

  /** The current theme */
  theme$: BehaviorSubject<'light' | 'dark'>;

  /** To minimize human errors, it has been decided to differentiate the colors from the Admin dashboard in production only */
  isAdminProd: boolean;

  getDeployUrl(suffixIfNotEmpty: string = ''): string {
    return this.deployUrl ? this.deployUrl + suffixIfNotEmpty : '';
  }

  changeTheme(theme: 'dark' | 'light'): void {
    // Set the theme on the app component
    this.theme$.next(theme);

    const themePrefix = 'app-theme-';
    // Set the theme on body element for global scope
    this.renderer.removeClass(this.document.body, `${themePrefix}dark`);
    this.renderer.removeClass(this.document.body, `${themePrefix}light`);
    this.renderer.addClass(this.document.body, `${themePrefix}${theme}`);

    // Set the theme on the overlay container also (to apply it on dialogs and other components that use the overlay)
    const overlayContainerClasses = this.overlayContainer.getContainerElement().classList;
    const themeClassesToRemove = Array.from(overlayContainerClasses).filter((item: string) =>
      item.includes(themePrefix)
    );
    if (themeClassesToRemove.length) {
      overlayContainerClasses.remove(...themeClassesToRemove);
    }
    overlayContainerClasses.add(themePrefix + theme);
    overlayContainerClasses.add('typography');
  }

  private getBooleanFlag(value: any): boolean {
    return typeof value === 'string' ? (<string>value).toLowerCase() === 'true' : !!value;
  }
}
